import { useCallback, useEffect, useRef } from 'react'
import { trackAnalyticsEvent } from '../lib/analytics'

type PendingNode = { id: number; node: Element }

export function useArtworkImpressions(context: string) {
  const observerRef = useRef<IntersectionObserver | null>(null)
  const seenRef = useRef(new Set<number>())
  const pendingRef = useRef<PendingNode[]>([])

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          const rawId = entry.target.getAttribute('data-artwork-id')
          const artworkId = rawId ? Number(rawId) : NaN
          if (!artworkId || Number.isNaN(artworkId) || seenRef.current.has(artworkId)) {
            observerRef.current?.unobserve(entry.target)
            return
          }
          seenRef.current.add(artworkId)
          trackAnalyticsEvent('artwork_impression', { artworkId, metadata: { context } })
          observerRef.current?.unobserve(entry.target)
        })
      },
      { threshold: 0.5 },
    )
    const observer = observerRef.current
    pendingRef.current.forEach(({ id, node }) => {
      node.setAttribute('data-artwork-id', String(id))
      observer.observe(node)
    })
    pendingRef.current = []
    return () => observer.disconnect()
  }, [context])

  return useCallback(
    (artworkId: number) => (node: Element | null) => {
      if (!node) return
      const observer = observerRef.current
      if (observer) {
        node.setAttribute('data-artwork-id', String(artworkId))
        observer.observe(node)
      } else {
        pendingRef.current.push({ id: artworkId, node })
      }
    },
    [],
  )
}
